/**
 * ENG-6088 — bounded auto-resume for circuit-breaker pauses.
 *
 * A tripped breaker (ENG-5441) pauses the agent until an operator clicks
 * Resume — every paused-agent hour is an hour of zero Conversation scores.
 * This module decides, on each manager poll, whether a tripped agent has
 * earned ONE automatic resume:
 *
 *   - the trip driver must have gone quiet: no restart event for
 *     `quietMs` (default 30 min). While paused the manager spawns nothing,
 *     so "quiet" is effectively time-since-trip — the resume is a bounded
 *     probe: if the driver storms again, the breaker re-trips within its
 *     own window and the agent stays paused.
 *   - one credit per trip cycle: after an auto-resume, a re-trip is NOT
 *     auto-resumed again until either an operator resumes (clears the
 *     marker — deliberate human intent resets the credit) or the backoff
 *     TTL (`backoffResetMs`, default 24h) expires. The TTL exists so a
 *     genuinely-unrelated trip weeks later isn't hostage to a marker from
 *     a long-resolved incident, while still bounding unattended resumes
 *     to at most one per TTL window.
 *
 * Pure state machine, no I/O — manager-worker.ts owns the API call, the
 * marker map, and its serialisation into manager-state.json (same pattern
 * as RestartBreaker). Tests drive `now` directly.
 *
 * Rollout: flag-gated OFF by default (`AGT_AUTO_RESUME_ENABLED`), matching
 * the auto-pause (AUGMENTED_AUTO_PAUSE_ENABLED) and quarantine
 * (AGT_CHANNEL_QUARANTINE_MODE) precedents — flip per-host via
 * /etc/agt/agt-manager.env after a canary bake.
 */

import type { TripState } from './restart-breaker.js';
import { readEnvNumber } from './restart-breaker.js';

/** Persisted per-agent marker: this trip cycle already used its credit. */
export interface AutoResumeRecord {
  /** trippedAt of the trip that was auto-resumed (forensics + dedupe). */
  trippedAt: number;
  /** When the manager issued the auto-resume. TTL anchor. */
  autoResumedAt: number;
}

export interface AutoResumeConfig {
  enabled: boolean;
  /** Driver must be quiet this long before the one auto-resume fires. */
  quietMs: number;
  /** A used credit expires after this long, re-arming auto-resume. */
  backoffResetMs: number;
}

const DEFAULT_QUIET_MS = 1_800_000; // 30 min
const DEFAULT_BACKOFF_RESET_MS = 86_400_000; // 24 h

export function readAutoResumeConfig(env: NodeJS.ProcessEnv = process.env): AutoResumeConfig {
  const raw = (env['AGT_AUTO_RESUME_ENABLED'] ?? '').trim().toLowerCase();
  return {
    enabled: raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on',
    quietMs: readEnvNumber('AGT_AUTO_RESUME_QUIET_MS', DEFAULT_QUIET_MS),
    backoffResetMs: readEnvNumber('AGT_AUTO_RESUME_BACKOFF_RESET_MS', DEFAULT_BACKOFF_RESET_MS),
  };
}

export type AutoResumeDecision =
  | { action: 'resume'; quietMs: number }
  | {
      action: 'skip';
      reason: 'disabled' | 'no-trip' | 'already-auto-resumed' | 'not-quiet-yet';
      /** For 'not-quiet-yet': ms until the quiet threshold is reached. */
      remainingMs?: number;
    };

/**
 * Decide whether a tripped, paused agent should be auto-resumed now.
 *
 * `marker` is the persisted {@link AutoResumeRecord} for this agent (if
 * any). A live marker means the credit is spent — stay paused until an
 * operator resumes or the TTL re-arms it. The caller must clear the marker
 * on operator resume (paused → active not initiated by us).
 */
export function decideAutoResume(opts: {
  trip: TripState | undefined;
  marker: AutoResumeRecord | undefined;
  config: AutoResumeConfig;
  now: number;
}): AutoResumeDecision {
  const { trip, marker, config, now } = opts;
  if (!config.enabled) return { action: 'skip', reason: 'disabled' };
  if (!trip) return { action: 'skip', reason: 'no-trip' };

  if (marker && now - marker.autoResumedAt < config.backoffResetMs) {
    return { action: 'skip', reason: 'already-auto-resumed' };
  }

  // Quiet anchor: the most recent restart event the breaker saw (falls
  // back to trippedAt — hydrated trips always carry it). While paused no
  // sessions run, so no further events accrue and this is monotone.
  const lastEventAt = trip.eventsAtTrip.length > 0
    ? Math.max(trip.trippedAt, ...trip.eventsAtTrip.map((e) => e.at))
    : trip.trippedAt;
  const quietFor = now - lastEventAt;
  if (quietFor < config.quietMs) {
    return { action: 'skip', reason: 'not-quiet-yet', remainingMs: config.quietMs - quietFor };
  }

  return { action: 'resume', quietMs: quietFor };
}

/**
 * Rehydrate persisted markers from manager-state.json. Mirrors
 * RestartBreaker.hydrate's defensive shape-check — a corrupt entry is
 * dropped rather than poisoning the map.
 */
export function hydrateAutoResumeMarkers(
  saved: Record<string, AutoResumeRecord> | null | undefined,
): Map<string, AutoResumeRecord> {
  const map = new Map<string, AutoResumeRecord>();
  if (!saved) return map;
  for (const [codeName, rec] of Object.entries(saved)) {
    if (rec && typeof rec.trippedAt === 'number' && typeof rec.autoResumedAt === 'number') {
      map.set(codeName, rec);
    }
  }
  return map;
}
